import { useDispatch } from 'react-redux';
import { API_REQUEST, showDialog } from '../../../core/actions';
import * as AssetsAPI from '../../../core/api/assets';
import { addInventoryItem } from '../../inventory/actions';
import {
  AssetScope,
  DrawerFormState,
} from '../../taskTemplates/components/TaskTemplateAdminDrawer';
import {
  closeNodeActionDrawer,
  fetchAssetGroups,
  fetchAssetItems,
  fetchAssetTemplates,
  fetchAssetSiteInventories,
  fetchAssetTaskOccurrences,
  fetchAssetTemplateOccurrence,
  fetchAssetTemplateOccurrences,
  updateAssetTemplateOccurrence,
} from '../actions';
import { getGroupDisplayName, getItemDisplayName } from '../helpers';

/** Templates own inventory directly; sub-assets own it through their own row. */
const inventoryScopeTemplateId = (node: any): number | null => {
  if (node?.type !== 'template') return null;
  const raw = node?.rawId ?? node?.raw?.id ?? node?.data?.id ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

interface NodeActionSubmitDeps {
  intl: any;
  site: any;
  isSiteManagerView: boolean;
  nodeActionDrawer: any;
  selectedSidebarItem: any;
  selectedOccurrenceId: number | null;
  activeOccurrenceId: number | null;
  groupOccurrenceIdByGroupId: Map<number, number>;
  itemOccurrenceIdByItemId: Map<number, number>;
  refreshOpenAssetView: () => void;
  handleSelectTemplate: (templateId: number) => void;
  setTaskDrawerAssetScope: (scope: AssetScope | undefined) => void;
  setTaskDrawerAddDefaults: (defaults: Partial<DrawerFormState> | undefined) => void;
  setTaskDrawerTitle: (title: string | undefined) => void;
  setTaskDrawerOpen: (open: boolean) => void;
}

/**
 * Everything the add/link drawer can submit: linking or creating tasks and
 * inventory, adding groups and sub-assets, and the edit branches.
 *
 * The scoping rule lives here. On a deployment every write carries occurrence
 * keys and no blueprint ids, so a site addition never reaches the master
 * template that other sites share.
 */
export function useNodeActionSubmit({
  intl,
  site,
  isSiteManagerView,
  nodeActionDrawer,
  selectedSidebarItem,
  selectedOccurrenceId,
  activeOccurrenceId,
  groupOccurrenceIdByGroupId,
  itemOccurrenceIdByItemId,
  refreshOpenAssetView,
  handleSelectTemplate,
  setTaskDrawerAssetScope,
  setTaskDrawerAddDefaults,
  setTaskDrawerTitle,
  setTaskDrawerOpen,
}: NodeActionSubmitDeps) {
  const dispatch = useDispatch();
  const isGroupOrAssetContext = !!selectedSidebarItem;

  const handleNodeActionSubmit = (mode: string, itemType: string | null, data: any) => {
    const siteId = site;

    if (mode === 'add' && data && data.__link) {
      const drawerNode = nodeActionDrawer?.node;
      let scopeType: 'group' | 'item' | 'template' | null = null;
      let scopeId: number | null = null;
      if (selectedSidebarItem) {
        scopeType = selectedSidebarItem.itemType as 'group' | 'item';
        scopeId = selectedSidebarItem.id;
      } else if (
        drawerNode?.type === 'group' ||
        drawerNode?.type === 'item' ||
        drawerNode?.type === 'template'
      ) {
        scopeType = drawerNode.type;
        scopeId = drawerNode.rawId ?? drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
      }
      const occurrenceId = Number(data.linkOccurrenceId);
      if (!scopeType || !scopeId || !Number.isFinite(occurrenceId) || occurrenceId <= 0) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const patchBody: any = { id: occurrenceId };
      if (activeOccurrenceId) {
        // Linking from a deployment attaches the task to that site's
        // occurrence. Writing the blueprint FKs here is what put linked
        // tasks on the master template for every site.
        patchBody.assetTemplateOccurrenceId = activeOccurrenceId;
        patchBody.assetGroupOccurrenceId =
          scopeType === 'group' ? groupOccurrenceIdByGroupId.get(Number(scopeId)) ?? null : null;
        patchBody.assetItemOccurrenceId =
          scopeType === 'item' ? itemOccurrenceIdByItemId.get(Number(scopeId)) ?? null : null;
      } else if (scopeType === 'group') patchBody.assetGroupId = scopeId;
      else if (scopeType === 'template') patchBody.assetTemplateId = scopeId;
      else patchBody.assetItemId = scopeId;
      // Through the saga so a failure is toasted (and the drawer stays open to
      // retry) and an expired token is refreshed rather than failing silently.
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchTaskOccurrence,
        data: patchBody,
        options: { siteId },
        action: 'LINK_TASK_TO_ASSET',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
          dispatch(fetchAssetTaskOccurrences());
          refreshOpenAssetView();
        },
      });
      return;
    }

    if (mode === 'add' && data && data.__linkInventory) {
      const drawerNode = nodeActionDrawer?.node;
      const inventoryId = Number(data.linkInventoryId);
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        dispatch(closeNodeActionDrawer());
        return;
      }

      const refreshAfterInventoryChange = () => {
        dispatch(closeNodeActionDrawer());
        dispatch(fetchAssetGroups());
        dispatch(fetchAssetItems());
        dispatch(fetchAssetSiteInventories());
        dispatch(fetchAssetTemplateOccurrences());
        refreshOpenAssetView();
      };

      const scopeTemplateId = inventoryScopeTemplateId(drawerNode);

      let scopeItemId: number | null = null;
      if (selectedSidebarItem?.itemType === 'item') {
        scopeItemId = selectedSidebarItem.id;
      } else if (drawerNode?.type === 'item') {
        scopeItemId = drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
      }

      // On a deployment the part belongs to that site, so it is attached by
      // occurrence. Blueprint links here would reach every site running it.
      if (activeOccurrenceId) {
        dispatch({
          type: API_REQUEST,
          callAPI: AssetsAPI.patchInventory,
          data: {
            id: inventoryId,
            assetTemplateOccurrenceId: activeOccurrenceId,
            assetItemOccurrenceId: scopeItemId
              ? itemOccurrenceIdByItemId.get(Number(scopeItemId)) ?? null
              : null,
          },
          options: { siteId },
          action: 'LINK_INVENTORY_TO_OCCURRENCE',
          voidCallback: refreshAfterInventoryChange,
        });
        return;
      }

      if (scopeTemplateId) {
        dispatch({
          type: API_REQUEST,
          callAPI: AssetsAPI.linkTemplateInventory,
          data: { assetTemplateId: scopeTemplateId, inventoryId },
          options: { siteId },
          action: 'LINK_INVENTORY_TO_TEMPLATE',
          voidCallback: refreshAfterInventoryChange,
        });
        return;
      }

      if (!scopeItemId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchInventory,
        data: { id: inventoryId, assetItemId: scopeItemId },
        options: { siteId },
        action: 'LINK_INVENTORY_TO_ASSET',
        voidCallback: refreshAfterInventoryChange,
      });
      return;
    }

    if (mode === 'add' && data && data.__openInventoryEditor) {
      const drawerNode = nodeActionDrawer?.node;
      const scopeTemplateId = inventoryScopeTemplateId(drawerNode);
      let scopeItemId: number | null = null;
      if (selectedSidebarItem?.itemType === 'item') {
        scopeItemId = selectedSidebarItem.id;
      } else if (drawerNode?.type === 'item') {
        scopeItemId = drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
      }
      dispatch(closeNodeActionDrawer());
      if (!activeOccurrenceId && !scopeTemplateId && !scopeItemId) return;

      // A part created on a deployment is that site's: it carries occurrence
      // keys and no blueprint link, so no other site inherits it.
      const assetScopeFields = activeOccurrenceId
        ? {
            assetTemplateOccurrenceId: activeOccurrenceId,
            assetItemOccurrenceId: scopeItemId
              ? itemOccurrenceIdByItemId.get(Number(scopeItemId)) ?? null
              : null,
          }
        : scopeTemplateId
        ? { assetTemplateId: scopeTemplateId }
        : { assetItemId: scopeItemId };

      dispatch(
        showDialog('UPSERT_INVENTORY', {
          isNew: true,
          intl,
          title: intl.formatMessage({ id: 'addInventoryItem' }),
          onSubmit: ({ item }: any) =>
            dispatch(
              addInventoryItem({
                item: { ...item, ...assetScopeFields },
                callback: [
                  () => fetchAssetGroups(),
                  () => fetchAssetItems(),
                  () => fetchAssetSiteInventories(),
                ],
                voidCallback: () => refreshOpenAssetView(),
              })
            ),
        })
      );
      return;
    }

    if (mode === 'add' && data && data.__openTaskEditor) {
      const drawerNode = nodeActionDrawer?.node;
      let scopeType: 'group' | 'item' | 'template' | null = null;
      let scopeId: number | null = null;
      let scopeName = '';
      if (selectedSidebarItem) {
        scopeType = selectedSidebarItem.itemType as 'group' | 'item';
        scopeId = selectedSidebarItem.id;
        scopeName =
          selectedSidebarItem.itemType === 'group'
            ? getGroupDisplayName(selectedSidebarItem.data)
            : getItemDisplayName(selectedSidebarItem.data);
      } else if (
        drawerNode?.type === 'group' ||
        drawerNode?.type === 'item' ||
        drawerNode?.type === 'template'
      ) {
        scopeType = drawerNode.type;
        scopeId = drawerNode.rawId ?? drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
        scopeName = drawerNode.name || '';
      }
      if (!scopeType || !scopeId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const typeByCard: Record<string, string> = {
        dailyTask: 'CleaningList',
        prevMaint: 'PreventativeMaintenance',
        project: 'Project',
      };
      const addDefaults: Partial<DrawerFormState> = {
        type: typeByCard[itemType || 'dailyTask'] || 'CleaningList',
      };
      if (itemType === 'project') addDefaults.interval = 'Once';
      // On a deployment the task belongs to this site alone, so the scope
      // carries occurrence ids and the blueprint ids are left off entirely.
      const scope: AssetScope = activeOccurrenceId
        ? {
            assetTemplateOccurrenceId: activeOccurrenceId,
            assetGroupOccurrenceId:
              scopeType === 'group'
                ? groupOccurrenceIdByGroupId.get(Number(scopeId)) ?? null
                : null,
            assetItemOccurrenceId:
              scopeType === 'item' ? itemOccurrenceIdByItemId.get(Number(scopeId)) ?? null : null,
          }
        : // On the catalog with no occurrence resolved, attach nothing rather
        // than writing blueprint links every site would inherit.
        isSiteManagerView
        ? {}
        : scopeType === 'group'
        ? { assetGroupId: scopeId }
        : scopeType === 'template'
        ? { assetTemplateId: scopeId }
        : { assetItemId: scopeId };
      dispatch(closeNodeActionDrawer());
      setTaskDrawerAssetScope(scope);
      setTaskDrawerAddDefaults(addDefaults);
      setTaskDrawerTitle(
        intl.formatMessage({ id: 'assets.drawer.addTo' }, { nodeName: scopeName || '' })
      );
      setTaskDrawerOpen(true);
      return;
    }

    const occurrenceTypeByCard: Record<string, string> = {
      dailyTask: 'daily',
      prevMaint: 'preventive_maintenance',
      project: 'project',
    };
    const priorityLevel: Record<string, number> = { low: 1, high: 3, critical: 4 };

    if (mode === 'add' && itemType && occurrenceTypeByCard[itemType] && isGroupOrAssetContext) {
      const instanceData: any = {
        name: data.name,
        type: occurrenceTypeByCard[itemType],
        status: 0,
      };
      // Only preventive maintenance carries a priority; anything unrecognised is medium.
      if (itemType === 'prevMaint') {
        instanceData.priority = priorityLevel[data.priority?.toLowerCase()] ?? 2;
      }
      if (selectedSidebarItem.itemType === 'group') {
        instanceData.assetGroupId = selectedSidebarItem.id;
      } else if (selectedSidebarItem.itemType === 'item') {
        instanceData.assetItemId = selectedSidebarItem.id;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postTaskOccurrence,
        data: instanceData,
        options: { siteId },
        action: 'CREATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'add' && itemType === 'inventory') {
      dispatch(closeNodeActionDrawer());
    } else if (mode === 'addGroup') {
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postAssetGroup,
        data: {
          name: data.name,
          notes: data.description || null,
        },
        options: { siteId },
        action: 'CREATE_ASSET_GROUP',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          refreshOpenAssetView();
        },
      });
    } else if (mode === 'addAsset') {
      const itemData: any = {
        name: data.name,
        notes: data.notes || null,
      };
      if (data.assetGroupId) {
        itemData.assetGroupId = Number(data.assetGroupId);
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postAssetItem,
        data: itemData,
        options: { siteId },
        action: 'CREATE_ASSET_ITEM',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetItems());
          refreshOpenAssetView();
        },
      });
    } else if (mode === 'editTask') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.raw?.id || (ed?.id ? String(ed.id).replace('task-', '') : null);
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const patchData: any = {
        id: Number(rawId),
        name: data.name,
        status: data.status != null ? parseInt(data.status, 10) : undefined,
        instructions: data.description || null,
      };
      const rawType = (ed?.raw?.type || '').toLowerCase();
      if (rawType === 'preventive_maintenance' && data.priority) {
        const priorityKey = String(data.priority).toLowerCase();
        patchData.priority =
          priorityKey === 'critical'
            ? 4
            : priorityKey === 'high'
            ? 3
            : priorityKey === 'low'
            ? 1
            : 2;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchTaskOccurrence,
        data: patchData,
        options: { siteId },
        action: 'UPDATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'editTemplate') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      // Status is the site's, not the blueprint's, so it patches the open
      // occurrence rather than travelling with the template fields.
      const applyStatusChange = () => {
        if (!selectedOccurrenceId || data.status === undefined) return;
        dispatch(
          updateAssetTemplateOccurrence(selectedOccurrenceId, { status: data.status }, () => {
            dispatch(fetchAssetTemplateOccurrences());
            dispatch(fetchAssetTemplateOccurrence(selectedOccurrenceId));
          })
        );
      };

      // The image is its own request either way: upload replaces whatever the
      // template had, and clearing it deletes the link.
      const applyImageChange = () => {
        if (data.imageFile) {
          dispatch({
            type: API_REQUEST,
            callAPI: AssetsAPI.postAssetTemplateImage,
            data: { id: Number(rawId), file: data.imageFile },
            action: 'UPLOAD_ASSET_TEMPLATE_IMAGE',
            voidCallback: () => dispatch(fetchAssetTemplates()),
          });
        } else if (data.imageRemoved) {
          dispatch({
            type: API_REQUEST,
            callAPI: AssetsAPI.deleteAssetTemplateImage,
            data: { id: Number(rawId) },
            action: 'DELETE_ASSET_TEMPLATE_IMAGE',
            voidCallback: () => dispatch(fetchAssetTemplates()),
          });
        }
      };

      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetTemplate,
        data: {
          id: Number(rawId),
          name: data.name,
          type: data.type,
          version: data.version || 'v1',
          description: data.description || null,
          defaultStatus: data.defaultStatus,
          canTriggerWashdown: Boolean(data.canTriggerWashdown),
        },
        options: { siteId },
        action: 'UPDATE_ASSET_TEMPLATE',
        voidCallback: () => {
          applyImageChange();
          applyStatusChange();
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetTemplates());
          handleSelectTemplate(Number(rawId));
        },
      });
    } else if (mode === 'editGroup') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetGroup,
        data: {
          id: Number(rawId),
          name: data.name,
          notes: data.description || null,
          position: data.position,
          installedAt: data.installedAt,
        },
        options: { siteId },
        action: 'UPDATE_ASSET_GROUP',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          refreshOpenAssetView();
        },
      });
    } else if (mode === 'editAsset') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const patchData: any = {
        id: Number(rawId),
        name: data.name,
        notes: data.notes || null,
        position: data.position,
      };
      if (data.assetGroupId) {
        patchData.assetGroupId = Number(data.assetGroupId);
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetItem,
        data: patchData,
        options: { siteId },
        action: 'UPDATE_ASSET_ITEM',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetItems());
          dispatch(fetchAssetGroups());
          refreshOpenAssetView();
        },
      });
    } else {
      dispatch(closeNodeActionDrawer());
    }
  };

  return handleNodeActionSubmit;
}
